import { Controller } from "@hotwired/stimulus"
import { select } from "d3-selection"
import { buildHierarchy, toggleCollapse } from "../tree/layout"
import { renderNodes, renderLinks, updateStates, setStateLabels } from "../tree/renderer"
import { setupZoom, zoomIn, zoomOut, resetZoom, fitAll, zoomToNode } from "../tree/interactions"
import { zoomIdentity } from "d3-zoom"
import { showPopover, hidePopover, showTooltip, hideTooltip, setupCloseHandlers, cleanupCloseHandlers } from "../tree/popover"
import { renderMiniMap, syncViewport, removeMiniMap } from "../tree/mini_map"
import { buildAncestorPaths, applyHighlight, clearHighlight } from "../tree/highlight"
import { showQuickAddOverlay, clearQuickAddOverlay } from "../tree/quick_add_overlay"

export default class extends Controller {
  static values = {
    url: String,
    focusMode: { type: Boolean, default: false },
    minimap: { type: Boolean, default: true },
    stateLabels: { type: Object, default: {} },
    guestMode: { type: Boolean, default: false }
  }
  static targets = ["svg", "loading", "empty", "minimapContainer", "error", "srAnnounce", "fullscreenBtn", "drawer", "drawerContent"]

  connect() {
    setStateLabels(this.stateLabelsValue)
    this.showLoading()
    this.fetchData()
    this._claimConfirmedHandler = this.handleClaimConfirmed.bind(this)
    document.addEventListener("claim-event:confirmed", this._claimConfirmedHandler)
    this._zoomToPersonHandler = this.handleZoomToPerson.bind(this)
    this.element.addEventListener("tree-search:zoomToPerson", this._zoomToPersonHandler)
    this._quickAddHandler = this.handleQuickAddOpen.bind(this)
    this.element.addEventListener("tree-quick-add:open", this._quickAddHandler)
    this._dragDroppedHandler = this.handleDragDropped.bind(this)
    this.element.addEventListener("tree-drag:dropped", this._dragDroppedHandler)
  }

  disconnect() {
    hidePopover()
    cleanupCloseHandlers()
    if (this._keydownHandler) {
      this.svgTarget.removeEventListener("keydown", this._keydownHandler)
      this._keydownHandler = null
    }
    if (this._fullscreenHandler) {
      document.removeEventListener("fullscreenchange", this._fullscreenHandler)
    }
    if (this._claimConfirmedHandler) {
      document.removeEventListener("claim-event:confirmed", this._claimConfirmedHandler)
      this._claimConfirmedHandler = null
    }
    if (this._zoomToPersonHandler) {
      this.element.removeEventListener("tree-search:zoomToPerson", this._zoomToPersonHandler)
      this._zoomToPersonHandler = null
    }
    if (this._quickAddHandler) {
      this.element.removeEventListener("tree-quick-add:open", this._quickAddHandler)
      this._quickAddHandler = null
    }
    if (this._dragDroppedHandler) {
      this.element.removeEventListener("tree-drag:dropped", this._dragDroppedHandler)
      this._dragDroppedHandler = null
    }
  }

  handleDragDropped(event) {
    const { sourceId, targetId, zone } = event.detail || {}
    if (!sourceId || !targetId || !zone || !this.hasDrawerTarget) return
    const treeId = this.urlValue.match(/\/trees\/(\d+)\//)?.[1]
    if (!treeId) return
    const url = this.buildDropUrl(treeId, sourceId, targetId, zone)
    if (url) {
      hidePopover()
      this.loadInDrawer(url)
    }
  }

  buildDropUrl(treeId, sourceId, targetId, zone) {
    if (zone === "top") {
      return `/trees/${treeId}/parentages/new?parent_id=${sourceId}&child_id=${targetId}`
    }
    if (zone === "bottom") {
      return `/trees/${treeId}/parentages/new?parent_id=${targetId}&child_id=${sourceId}`
    }
    return `/trees/${treeId}/unions/new?person_1_id=${sourceId}&person_2_id=${targetId}`
  }

  handleQuickAddOpen(event) {
    const url = event.detail?.url
    if (!url || !this.hasDrawerTarget) return
    hidePopover()
    this.loadInDrawer(url)
  }

  handleZoomToPerson(event) {
    const nodeKey = event.detail?.nodeKey
    const personId = event.detail?.personId
    if (!nodeKey || !this.root) return
    const ok = zoomToNode(select(this.svgTarget), this.zoomBehavior, this.root, nodeKey)
    if (ok && personId) this.pulseNode(personId)
  }

  pulseNode(personId) {
    const node = this.element.querySelector(`.tree-node[data-person-id="${personId}"]`)
    if (!node) return
    node.classList.add("tree-node--focus-pulse")
    setTimeout(() => node.classList.remove("tree-node--focus-pulse"), 2000)
  }

  handleClaimConfirmed(event) {
    const personId = event.detail?.personId
    if (!personId) return
    const node = this.element.querySelector(`[data-person-id="${personId}"]`)
    if (node) {
      node.classList.add("claimed-pulse")
    }
  }

  async fetchData() {
    try {
      const response = await fetch(this.urlValue, {
        headers: { "Accept": "application/json" }
      })
      if (!response.ok) throw new Error(`HTTP ${response.status}`)
      this.treeData = await response.json()
      this.render()
    } catch (error) {
      console.error("Tree data fetch failed:", error)
      this.hideLoading()
      this.showError()
    }
  }

  render() {
    this.hideLoading()
    this.hideError()

    if (!this.treeData.people.length) {
      this.showEmpty()
      return
    }

    const root = buildHierarchy(this.treeData)
    if (!root) { this.showEmpty(); return }

    this.root = root
    this.fullData = this.treeData

    if (!this._initialLoadDone) {
      this.focusModeValue = this.treeData.focusMode
      this._initialLoadDone = true
    }

    this.renderTree(root)
  }

  renderTree(root) {
    const svg = select(this.svgTarget)
    svg.selectAll("*").remove()
    const g = svg.append("g").attr("class", "tree-graph__content")

    this.zoomBehavior = setupZoom(svg, g, {
      onZoomStart: () => { hidePopover(); hideTooltip() }
    })

    // Sync minimap on zoom
    this.setupZoomSync(svg)

    renderLinks(g, root, this.treeData)
    const nodes = renderNodes(g, root)
    this.ancestorPaths = buildAncestorPaths(root)
    this.setupNodeEvents(nodes)
    setupCloseHandlers(this.svgTarget)

    if (this.focusModeValue) {
      this.centerOnRoot(svg, g, root)
    } else {
      fitAll(svg, this.zoomBehavior)
    }
    this.announceTree()
    this.setupKeyboardNav()

    if (this.minimapValue && this.hasMinimapContainerTarget) {
      this.miniMapState = renderMiniMap(
        this.minimapContainerTarget, root, svg, this.zoomBehavior
      )
    }
  }

  setupZoomSync(svg) {
    const origZoom = this.zoomBehavior
    const origHandler = origZoom.on("zoom")
    origZoom.on("zoom", (event) => {
      origHandler(event)
      this.updateMiniMapViewport()
    })
    svg.call(origZoom)
  }

  updateMiniMapViewport() {
    if (!this.miniMapState || !this.hasMinimapContainerTarget) return
    const { viewport, bounds, scale, offsetX, offsetY } = this.miniMapState
    syncViewport(viewport, select(this.svgTarget), bounds, scale, offsetX, offsetY)
  }

  setupNodeEvents(nodes) {
    const isMobile = window.innerWidth < 768

    // Collapse/expand on collapse button click
    nodes.selectAll(".tree-node__collapse-btn").on("click", (event, _d) => {
      event.stopPropagation()
      const btn = event.currentTarget
      const nodeKey = btn.getAttribute("data-node-key")
      const unionKey = btn.getAttribute("data-union-key") || null
      this.handleCollapse(nodeKey, unionKey)
    })

    if (this.guestModeValue) {
      // Guest mode: placeholder "?" nodes are clickable, no popovers
      nodes.filter(d => d.state === "placeholder").on("click", (event, d) => {
        event.stopPropagation()
        const contributionCtrl = this.element.closest("[data-controller~='contribution']")
        if (!contributionCtrl) return
        const ctrl = this.application.getControllerForElementAndIdentifier(
          contributionCtrl, "contribution"
        )
        if (ctrl) {
          ctrl.personSignedIdValue = d.parentSignedId || ""
          ctrl.openField(event)
        }
      })
      return
    }

    nodes.on("click", (event, d) => {
      if (event.target.classList?.contains("tree-node__collapse-btn")) return
      event.stopPropagation()
      hideTooltip()
      updateStates(select(this.svgTarget).select("g"), d.nodeKey)
      showPopover(d, this.svgTarget, event)
    })

    if (!isMobile) {
      nodes.on("mouseover", (event, d) => {
        showTooltip(d, event)
      })
      nodes.on("mouseout", () => {
        hideTooltip()
      })
    }

    nodes.on("mouseenter", (event, d) => {
      this.highlightLineage(d.nodeKey)
      const personId = d.nodeKey?.replace(/^p/, "")
      if (personId && d.state !== "placeholder") {
        showQuickAddOverlay(event.currentTarget, personId)
      }
    })
    nodes.on("mouseleave", (event) => {
      this.clearLineageHighlight()
      clearQuickAddOverlay(event.currentTarget)
    })

    nodes.on("keydown", (event, d) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault()
        showPopover(d, this.svgTarget, event)
      }
    })
  }

  handleCollapse(nodeKey, unionKey = null) {
    this.root = toggleCollapse(nodeKey, this.root, unionKey)
    this.renderTree(this.root)
  }

  highlightLineage(nodeKey) {
    const pathSet = this.ancestorPaths?.get(nodeKey)
    if (!pathSet) return
    applyHighlight(this.svgTarget, pathSet)
  }

  clearLineageHighlight() {
    clearHighlight(this.svgTarget)
  }

  centerOnRoot(svg, g, root) {
    const svgEl = this.svgTarget
    const cx = svgEl.clientWidth / 2
    const cy = 60
    const dx = cx - root.x
    const dy = cy - root.y
    svg.call(this.zoomBehavior.transform, zoomIdentity.translate(dx, dy))
  }

  zoomIn() { zoomIn(select(this.svgTarget), this.zoomBehavior) }
  zoomOut() { zoomOut(select(this.svgTarget), this.zoomBehavior) }
  resetView() { fitAll(select(this.svgTarget), this.zoomBehavior) }

  toggleFocus() {
    this.focusModeValue = !this.focusModeValue
    this.render()
  }

  toggleMinimap() {
    this.minimapValue = !this.minimapValue
    if (!this.minimapValue && this.hasMinimapContainerTarget) {
      removeMiniMap(this.minimapContainerTarget)
      this.miniMapState = null
    } else if (this.root && this.hasMinimapContainerTarget) {
      this.miniMapState = renderMiniMap(
        this.minimapContainerTarget, this.root,
        select(this.svgTarget), this.zoomBehavior
      )
    }
  }

  toggleFullscreen() {
    const container = this.element
    if (document.fullscreenElement) {
      document.exitFullscreen()
    } else {
      container.requestFullscreen()
      this.listenFullscreenExit()
    }
  }

  listenFullscreenExit() {
    this._fullscreenHandler = () => {
      const isFs = !!document.fullscreenElement
      this.svgTarget.style.height = isFs ? "100vh" : "500px"
      if (this.hasFullscreenBtnTarget) {
        this.fullscreenBtnTarget.textContent = isFs ? "Quitter" : "Plein écran"
      }
      if (!isFs && this.root) {
        fitAll(select(this.svgTarget), this.zoomBehavior)
      }
    }
    document.addEventListener("fullscreenchange", this._fullscreenHandler)
    this._fullscreenHandler()
  }

  async openDrawer(event) {
    event.preventDefault()
    const url = event.currentTarget.dataset.drawerUrl
    if (!url || !this.hasDrawerTarget) return

    hidePopover()
    this.loadInDrawer(url)
  }

  async loadInDrawer(url) {
    this.drawerContentTarget.innerHTML = '<div class="p-8 text-center text-muted animate-pulse">Chargement…</div>'
    this.drawerTarget.classList.remove("hidden")

    const resp = await fetch(url, {
      headers: { Accept: "text/html", "X-Drawer": "1" }
    })
    if (!resp.ok) return

    this.drawerContentTarget.innerHTML = await resp.text()
    this.interceptDrawerLinks()
  }

  interceptDrawerLinks() {
    this.drawerContentTarget.addEventListener("click", (e) => {
      const link = e.target.closest("a[href]")
      if (!link) return

      const href = link.getAttribute("href")
      if (!href || href.startsWith("#") || href.startsWith("http")) return

      // "Retour à l'arbre" link -> close drawer
      if (href === this.backToTreeUrl()) {
        e.preventDefault()
        this.closeDrawer()
        return
      }

      // All internal links -> load in drawer
      e.preventDefault()
      this.loadInDrawer(href)
    })

    this.drawerContentTarget.addEventListener("submit", (e) => {
      const form = e.target.closest("form")
      if (!form) return
      e.preventDefault()
      this.submitFormInDrawer(form)
    })
  }

  async submitFormInDrawer(form) {
    const resp = await fetch(form.action, {
      method: form.method || "POST",
      headers: { "X-Drawer": "1" },
      body: new FormData(form)
    })
    if (resp.redirected) {
      this.loadInDrawer(resp.url)
    } else if (resp.ok) {
      this.drawerContentTarget.innerHTML = await resp.text()
      this.interceptDrawerLinks()
    }
  }

  backToTreeUrl() {
    const treeMatch = window.location.pathname.match(/\/trees\/\d+/)
    return treeMatch ? treeMatch[0] : null
  }

  closeDrawer() {
    if (this.hasDrawerTarget) {
      this.drawerTarget.classList.add("hidden")
      this.drawerContentTarget.innerHTML = ""
    }
  }

  showError() {
    if (this.hasErrorTarget) {
      this.errorTarget.classList.remove("hidden")
      this.errorTarget.innerHTML =
        '<p>Impossible de charger l\u2019arbre.</p>' +
        '<button data-action="tree-graph#retry" class="btn btn--sm">Réessayer</button>'
    }
    if (this.hasSvgTarget) this.svgTarget.classList.add("hidden")
  }

  hideError() {
    if (this.hasErrorTarget) this.errorTarget.classList.add("hidden")
  }

  retry() {
    this.hideError()
    this.showLoading()
    this.fetchData()
  }

  announceTree() {
    if (!this.hasSrAnnounceTarget || !this.treeData) return
    let msg = `Arbre avec ${this.treeData.people.length} personnes`
    if (this.root?._orphanCount) {
      msg += `, dont ${this.root._orphanCount} sans lien`
    }
    this.srAnnounceTarget.textContent = msg
  }

  setupKeyboardNav() {
    if (this._keydownHandler) {
      this.svgTarget.removeEventListener("keydown", this._keydownHandler)
    }

    this._keydownHandler = (event) => {
      const arrows = ["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"]
      if (!arrows.includes(event.key)) return

      event.preventDefault()
      const nodes = [...this.svgTarget.querySelectorAll(".tree-node[tabindex]")]
      if (!nodes.length) return

      const current = document.activeElement?.closest(".tree-node[tabindex]")
      const idx = current ? nodes.indexOf(current) : -1

      let next
      if (event.key === "ArrowDown" || event.key === "ArrowRight") {
        next = idx < nodes.length - 1 ? idx + 1 : 0
      } else {
        next = idx > 0 ? idx - 1 : nodes.length - 1
      }

      nodes[next].focus()
    }

    this.svgTarget.addEventListener("keydown", this._keydownHandler)
  }

  showLoading() {
    if (this.hasLoadingTarget) this.loadingTarget.classList.remove("hidden")
    if (this.hasSvgTarget) this.svgTarget.classList.add("hidden")
    if (this.hasEmptyTarget) this.emptyTarget.classList.add("hidden")
  }

  hideLoading() {
    if (this.hasLoadingTarget) this.loadingTarget.classList.add("hidden")
    if (this.hasSvgTarget) this.svgTarget.classList.remove("hidden")
  }

  showEmpty() {
    if (this.hasSvgTarget) this.svgTarget.classList.add("hidden")
    if (this.hasEmptyTarget) this.emptyTarget.classList.remove("hidden")
  }
}
